import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MessageCircleIcon, SendIcon, XIcon } from 'lucide-react';
import { useAcademy } from '../../context/Academy';
import { useAcademyHub } from '../../context/AcademyHub';
import { inputClass } from '../ui/Field';
import { Button } from '../ui/Button';
import { isSupportOnline } from '../../utils/academyFormat';
import { validateEmail } from '../../lib/leads';
export function SupportChat() {
  const {
    state
  } = useAcademy();
  const {
    hub,
    sendChat,
    leaveMessage
  } = useAcademyHub();
  const reduced = useReducedMotion();
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [email, setEmail] = React.useState(state.learner?.email ?? '');
  const [error, setError] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const online = isSupportOnline();
  const listRef = React.useRef<HTMLOListElement>(null);
  const seen = React.useRef(hub.chat.length);
  const unreadSupport = !open && hub.chat.slice(seen.current).some(m => m.from === 'support');
  React.useEffect(() => {
    if (open) seen.current = hub.chat.length;
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight
    });
  }, [open, hub.chat.length]);
  React.useEffect(() => {
    if (state.learner?.email) setEmail(state.learner.email);
  }, [state.learner?.email]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    if (online) {
      sendChat(draft);
      setDraft('');
      return;
    }
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    setError('');
    setSending(true);
    await leaveMessage(email, draft);
    setSending(false);
    setDraft('');
  };
  return <div className="fixed bottom-5 right-5 z-40 flex flex-col items-end" data-id="element-537" data-mp="1jb5koa8t8utc">
      <AnimatePresence data-id="element-538" data-mp="1o8xljtrfnr9o">
        {open && <motion.section aria-label="Academy support chat" initial={reduced ? false : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduced ? undefined : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="mb-3 flex h-[min(520px,calc(100vh-7rem))] w-[min(380px,calc(100vw-2.5rem))] origin-bottom-right flex-col overflow-hidden rounded-md border border-line bg-panel shadow-2xl" data-id="element-539" data-mp="1o8pc6buir438">
            <header className="flex items-start justify-between gap-3 border-b border-line px-5 py-4" data-id="element-540" data-mp="1j4mvsc4krolu">
              <div data-id="element-541" data-mp="1e4r0r2duv7if">
                <h2 className="text-[15px] font-medium text-ink" data-id="element-542" data-mp="1e48xvmwotv1j">Academy support</h2>
                <p className="mt-0.5 flex items-center gap-2 text-small text-muted" data-id="element-543" data-mp="196yybefl42zz">
                  <span className={`h-2 w-2 rounded-full ${online ? 'bg-[#4ADE80]' : 'bg-muted'}`} aria-hidden="true" data-id="element-544" data-mp="2pvf2wgsk5sd" />
                  {online ? 'Team online, Mon to Fri 08:00 to 18:00 Kigali' : 'Offline. Leave a message and we will reply by email.'}
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close chat" className="-mr-2 flex h-9 w-9 items-center justify-center rounded-sm text-muted hover:bg-charcoal hover:text-ink" data-id="element-545" data-mp="2ed78nultfvsq">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-546" data-mp="2o53a9fn4241p" />
              </button>
            </header>

            <ol ref={listRef} className="flex-1 space-y-3 overflow-y-auto px-5 py-4" aria-live="polite" data-id="element-547" data-mp="2x3ybsef7mpyo">
              {hub.chat.length === 0 && <li className="text-[14px] text-body" data-id="element-548" data-mp="324cmy5c3yg0x">
                  Ask about a course, your progress, team enrolment or certificates. A person from the Academy team answers.
                </li>}
              {hub.chat.map(m => <li key={m.id} className={`flex ${m.from === 'learner' ? 'justify-end' : 'justify-start'}`} data-id="element-549" data-mp="1cnv2ckd9a5ss">
                  <p className={`max-w-[85%] rounded-md px-3.5 py-2.5 text-[14px] leading-snug ${m.from === 'learner' ? 'bg-gold text-charcoal' : m.from === 'support' ? 'bg-charcoal text-ink' : 'border border-dashed border-line text-muted'}`} data-id="element-550" data-mp="334p7tnq8wxhg">
                    {m.from === 'support' && <span className="mb-1 block text-[12px] text-royal" data-id="element-551" data-mp="2kez2w7srqvbt">Academy team</span>}
                    {m.body}
                  </p>
                </li>)}
            </ol>

            <form onSubmit={submit} className="space-y-2 border-t border-line p-3" data-id="element-552" data-mp="2t8c3zx0zl38z">
              {!online && <div data-id="element-553" data-mp="2oe7zrlj3v9bl">
                  <label htmlFor="chat-email" className="sr-only" data-id="element-554" data-mp="20qhy86z0lm1n">
                    Your email
                  </label>
                  <input id="chat-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Your email" aria-invalid={Boolean(error)} aria-describedby={error ? 'chat-email-error' : undefined} className={`${inputClass} py-2 text-[14px]`} data-id="element-555" data-mp="1wpxz4xuvs8cv" />
                  {error && <p id="chat-email-error" role="alert" className="mt-1 text-small text-[#FF8B7A]" data-id="element-556" data-mp="25b62x1fsyl94">
                      {error}
                    </p>}
                </div>}
              <div className="flex items-end gap-2" data-id="element-557" data-mp="1veb6co33auxh">
                <label htmlFor="chat-message" className="sr-only" data-id="element-558" data-mp="1vfm8kjgvzumh">
                  Message
                </label>
                <textarea id="chat-message" rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                e.currentTarget.form?.requestSubmit();
              }
            }} placeholder={online ? 'Type a message' : 'Your message'} className={`${inputClass} resize-none py-2 text-[14px]`} data-id="element-559" data-mp="jycsm6wyvkpx" />
                <Button type="submit" size="sm" disabled={!draft.trim() || sending} aria-label="Send message" className="h-10 w-10 shrink-0 !px-0" data-id="element-560" data-mp="2ctdxqg833fbp">
                  <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-561" data-mp="23th9gfplwih6" />
                </Button>
              </div>
            </form>
          </motion.section>}
      </AnimatePresence>

      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-label={open ? 'Close support chat' : 'Open support chat'} className="relative flex h-14 w-14 items-center justify-center rounded-full bg-gold text-charcoal shadow-xl transition-transform duration-150 ease-out hover:scale-[1.04] active:scale-[0.97]" data-id="element-562" data-mp="1tx5oz2fv4mbn">
        {open ? <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-563" data-mp="4mtnp7aiqbc0" /> : <MessageCircleIcon className="h-6 w-6" aria-hidden="true" data-id="element-564" data-mp="4lijx7h2p3kw" />}
        {unreadSupport && <span className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-2 border-charcoal bg-ink" aria-hidden="true" data-id="element-565" data-mp="9f3m7bpp8jpw" />}
      </button>
    </div>;
}